import { useSyncExternalStore } from "react";
import { syncThemeColorMeta } from "./theme";

/** Light / dark / follow the OS. The choice is per device (localStorage "pos.theme"; "system"
 * stores nothing). index.html runs the same resolution before first paint, so there is no
 * flash; this module keeps it in sync afterwards. */
export type ThemeChoice = "system" | "light" | "dark";
const KEY = "pos.theme";
const media = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
const listeners = new Set<() => void>();

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}
let choice: ThemeChoice = typeof window !== "undefined" ? read() : "system";

export const resolveTheme = (c: ThemeChoice): "light" | "dark" => (c === "system" ? (media?.matches ? "dark" : "light") : c);

function apply(): void {
  const el = document.documentElement;
  const t = resolveTheme(choice);
  el.dataset.theme = t;
  el.style.colorScheme = t;
  syncThemeColorMeta();
  listeners.forEach((l) => l());
}

export function setThemeChoice(c: ThemeChoice): void {
  choice = c;
  try {
    if (c === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, c);
  } catch {
    /* storage blocked: the choice lasts for this page only */
  }
  apply();
}

if (typeof window !== "undefined") {
  media?.addEventListener("change", () => choice === "system" && apply());
  // Another tab changed the choice.
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    choice = read();
    apply();
  });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export function useTheme(): { choice: ThemeChoice; resolved: "light" | "dark"; set: (c: ThemeChoice) => void } {
  const c = useSyncExternalStore(subscribe, () => choice, () => "system" as ThemeChoice);
  const resolved = useSyncExternalStore(subscribe, () => resolveTheme(choice), () => "light" as const);
  return { choice: c, resolved, set: setThemeChoice };
}
